import { array } from '@ember/helper';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';

import themed from 'webapp/decorators/themed';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import type { RadiobuttonItemInfo } from 'webapp/components/form/radiobutton';
import FormRadiobuttonComponent from 'webapp/components/form/radiobutton';

export interface Args {
	selected: AnySimpleValue;
	options: RadiobuttonItemInfo[];
	onChange: (value: AnySimpleValue) => void;
	value?: AnySimpleValue;
	name?: string;
	isDisabled?: boolean;
	highlightText?: boolean;
	isRequired?: boolean;
	theme?: string;
	size?: string;
}

interface FormRadiobuttonGroupComponentInterface<T> {
	Element: HTMLDivElement; // this is needed for ...attributes
	Args: T;
}

@themed()
export default class FormRadiobuttonGroupComponent<T extends Args> extends Component<
	FormRadiobuttonGroupComponentInterface<T>
> {
	guid = guidFor(this);

	declare theme: string; // from @themed
	declare size: string; // from @themed
	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	get uniqueName() {
		return `${this.args.name || 'radiobuttongroup'}-${this.guid}`;
	}

	get optionsCountClass() {
		const optCount = (this.options || []).length;
		return `opt-count-${optCount}`;
	}

	get options() {
		// bcos might be extended
		return this.args.options;
	}

	@action
	checkAutoselect() {
		if (!this.args.isRequired) {
			return;
		}
		if (this.args.highlightText) {
			return;
		}
		if (this.args.value || this.args.value === 0) {
			return;
		}

		const options = this.options;
		if (options.length === 1) {
			this.args.onChange(options[0]!.value);
		}
	}
	<template>
		<div
			...attributes
			class={{joinClasses
				(array "radiobutton-group" this.themeClass this.sizeClass this.optionsCountClass)
			}}
			{{didInsert this.checkAutoselect}}
			{{didUpdate this.checkAutoselect @isRequired @options @value @highlightText}}
		>
			{{#each this.options as |option|}}
				<FormRadiobuttonComponent
					@name={{this.uniqueName}}
					@selected={{@selected}}
					@value={{option.value}}
					@label={{option.label}}
					@label2={{option.label2}}
					@tooltipText={{option.tooltipText}}
					@isDisabled={{option.disabled}}
					@isGroupDisabled={{@isDisabled}}
					@item={{option}}
					@theme={{this.theme}}
					@size={{this.size}}
					@onChange={{@onChange}}
				/>
			{{/each}}
		</div>
	</template>
}
